import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import LikeButton from './LikeButton';
import { truncateContentWithMentions } from '../../../utils/mentionUtils';
import ContentWithMentions from '../../common/ContentWithMentions';
import { filterPresetTags } from '../../../utils/tagUtils';
import useCurrentProfilePicture from '../../../hooks/useCurrentProfilePicture';

const QuestionCard = ({ 
  question, 
  onLike, 
  className = '' 
}) => {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const contentRef = useRef(null);
  
  const {
    id,
    title,
    content,
    authorId,
    authorName,
    authorPhotoURL,
    createdAt,
    viewCount = 0,
    replyCount = 0,
    likeCount = 0,
    tags = []
  } = question;

  // Use dynamic profile picture that updates when user changes their photo
  const { currentProfilePicture } = useCurrentProfilePicture(authorId, authorPhotoURL);

  // Filter out preset tags for display
  const displayTags = filterPresetTags(tags);

  // Format the creation date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    
    return date.toLocaleDateString();
  };

  // Handle link clicks with event delegation
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleLinkClick = (e) => {
      // Check if the clicked element is a link with our data attribute
      const link = e.target.closest('a[data-external-link="true"]');
      if (link) {
        e.preventDefault();
        e.stopPropagation();
        const href = link.getAttribute('href');
        if (href) {
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      }
    };

    contentElement.addEventListener('click', handleLinkClick);
    
    return () => {
      contentElement.removeEventListener('click', handleLinkClick);
    };
  }, []);

  // Note: Content truncation is now handled by truncateContentWithMentions utility

  // Handle navigation with immediate feedback
  const handleNavigation = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNavigating(true);
    // Navigate in the same tab using React Router
    navigate(`/forum/${id}`);
  };



  return (
    <div
      onClick={handleNavigation}
      className={`block bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 
                 hover:bg-gray-50 dark:hover:bg-gray-750 forum-card-interactive
                 group cursor-pointer transition-colors duration-200 relative ${className}
                 ${isNavigating ? 'forum-card-navigating' : ''}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleNavigation(e);
        }
      }}
    >
      <div className="px-3 py-3 sm:px-4 sm:py-4">
        {/* Compact header with author and timestamp */}
        <div className="flex items-center space-x-2 mb-2">
          <button
            onClick={(e) => {
              console.log('QuestionCard: User profile image clicked');
              e.preventDefault();
              e.stopPropagation();
              navigate(`/network/${question.authorId}`);
            }}
            className="cursor-pointer hover:opacity-80 transition-opacity duration-200"
          >
            {currentProfilePicture ? (
              <img
                src={currentProfilePicture}
                alt={authorName}
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full ring-2 ring-gray-200 dark:ring-gray-700"
              />
            ) : (
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-purple-500 flex items-center justify-center">
                <span className="text-xs sm:text-sm font-medium text-white">
                  {(authorName || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </button>
          <div className="flex items-center space-x-1 text-xs sm:text-sm text-gray-500">
            <button
              onClick={(e) => {
                console.log('QuestionCard: User profile name clicked');
                e.preventDefault();
                e.stopPropagation();
                window.open(`/network/${question.authorId}`, '_blank', 'noopener');
              }}
              className="font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-200 bg-transparent border-none p-0 cursor-pointer"
            >
              {authorName}
            </button>
            <span>•</span>
            <span>{formatDate(createdAt)}</span>
          </div>
        </div>

        {/* Title - larger and more prominent */}
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white 
                       mb-2 line-clamp-2 leading-tight group-hover:text-purple-600 
                       dark:group-hover:text-purple-400 transition-colors duration-200 flex items-center">
          {title}
          {isNavigating && (
            <div className="ml-2 w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          )}
        </h3>

        {/* Content preview - smaller and more compact */}
        {content && (
          <div 
            ref={contentRef}
            className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2 leading-relaxed"
          >
            <ContentWithMentions 
              content={truncateContentWithMentions(content, 150)}
              className=""
            />
          </div>
        )}

        {/* Tags - more compact */}
        {displayTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {displayTags.slice(0, 2).map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs 
                          font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 
                          dark:text-purple-300"
              >
                {tag}
              </span>
            ))}
            {displayTags.length > 2 && (
              <span className="text-xs text-gray-500">+{displayTags.length - 2}</span>
            )}
          </div>
        )}

        {/* Engagement metrics - compact horizontal layout */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-xs sm:text-sm text-gray-500">
            <div className="flex items-center space-x-1">
              <EyeIcon className="w-4 h-4" />
              <span>{viewCount}</span>
            </div>
            <div className="flex items-center space-x-1">
              <ChatBubbleLeftIcon className="w-4 h-4" />
              <span>{replyCount}</span>
            </div>
          </div>
          
          <div onClick={(e) => e.stopPropagation()}>
            <LikeButton
              targetType="question"
              targetId={id}
              initialLikeCount={likeCount}
              authorId={question.authorId}
              onLikeUpdate={onLike ? (targetId, newLikeCount, isLiked) => onLike({ ...question, likeCount: newLikeCount }) : undefined}
              size="sm"
              className="p-1 border-none bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionCard;